import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { buscarJogosDaSemana } from '../lib/espn'
import { buscarPicksDaSemana } from '../lib/negocio'

const ANO = 2026
const POLL_MS = 30000

function labelSemana(sem) {
  if (sem.tipo === 'regular') return `Semana ${sem.numero}`
  const nomes = { wildcard: 'Wild Card', divisional: 'Divisional', conference: 'Conference', superbowl: 'Super Bowl' }
  return nomes[sem.tipo] || sem.tipo
}

function formatarHorario(isoString) {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return d.toLocaleString('pt-BR', {
    weekday: 'short', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  })
}

function statusJogo(jogo) {
  if (jogo.status === 'STATUS_FINAL') return { texto: 'Final', cor: '#666', vivo: false }
  if (jogo.status === 'STATUS_IN_PROGRESS') {
    const periodoTexto = jogo.periodo ? `${jogo.periodo}ºQ` : ''
    return { texto: `${periodoTexto} ${jogo.tempo_restante || ''}`.trim() || 'Em andamento', cor: '#A32D2D', vivo: true }
  }
  return { texto: formatarHorario(jogo.horario), cor: '#888', vivo: false }
}

// Retorna 'venceu' | 'perdeu' | null (jogo ainda não terminou) para o time informado
function resultadoDoTime(jogo, timeAbbr) {
  if (jogo.status !== 'STATUS_FINAL') return null
  if (jogo.resultado === 'empate') return 'perdeu'
  const venceu = (jogo.resultado === 'casa' && timeAbbr === jogo.time_casa) || (jogo.resultado === 'visitante' && timeAbbr === jogo.time_visitante)
  return venceu ? 'venceu' : 'perdeu'
}

export default function Placar() {
  const { ligaAtual } = useAuth()
  const [semanas, setSemanas] = useState([])
  const [semanaId, setSemanaId] = useState(null)
  const [jogos, setJogos] = useState([])
  const [picksDaSemana, setPicksDaSemana] = useState([])
  const [membros, setMembros] = useState([])
  const [loadingSemanas, setLoadingSemanas] = useState(true)
  const [loadingJogos, setLoadingJogos] = useState(false)
  const [jogoExpandidoId, setJogoExpandidoId] = useState(null)
  const [atualizadoEm, setAtualizadoEm] = useState(null)
  const pollRef = useRef(null)

  // Carrega a lista de semanas da liga e pré-seleciona a semana atual
  useEffect(() => {
    if (!ligaAtual?.id) return
    async function carregar() {
      setLoadingSemanas(true)
      const { data } = await supabase
        .from('semanas').select('id, numero, tipo, picks_revelados, picks_abertos, resultados_processados')
        .eq('liga_id', ligaAtual.id).order('numero', { ascending: true })
      setSemanas(data || [])
      if (data?.length) {
        const atual = data.find(s => s.picks_abertos) || data.find(s => !s.resultados_processados) || data[0]
        setSemanaId(atual.id)
      }
      setLoadingSemanas(false)
    }
    carregar()
  }, [ligaAtual?.id])

  const semanaSelecionada = semanas.find(s => s.id === semanaId)

  const carregarJogosEPicks = useCallback(async (mostrarLoading) => {
    if (!semanaSelecionada?.picks_revelados) return
    if (mostrarLoading) setLoadingJogos(true)
    const tipoTemporada = semanaSelecionada.tipo === 'regular' ? 2 : 3
    const [resJogos, resPicks] = await Promise.all([
      buscarJogosDaSemana(ANO, semanaSelecionada.numero, tipoTemporada),
      buscarPicksDaSemana(semanaSelecionada.id, ligaAtual.id),
    ])
    if (resJogos.sucesso) setJogos([...resJogos.jogos].sort((a, b) => new Date(a.horario) - new Date(b.horario)))
    if (resPicks.sucesso) { setPicksDaSemana(resPicks.picks); setMembros(resPicks.membros) }
    setAtualizadoEm(new Date())
    setLoadingJogos(false)
  }, [semanaSelecionada, ligaAtual?.id])

  // Busca inicial ao trocar de semana
  useEffect(() => {
    setJogoExpandidoId(null)
    setJogos([])
    setPicksDaSemana([])
    setMembros([])
    if (semanaSelecionada?.picks_revelados) carregarJogosEPicks(true)
    // eslint-disable-next-line
  }, [semanaId])

  // Polling automático de 30s enquanto houver jogo em andamento
  useEffect(() => {
    clearInterval(pollRef.current)
    const temJogoVivo = jogos.some(j => j.status === 'STATUS_IN_PROGRESS')
    if (temJogoVivo) {
      pollRef.current = setInterval(() => carregarJogosEPicks(false), POLL_MS)
    }
    return () => clearInterval(pollRef.current)
  }, [jogos, carregarJogosEPicks])

  function quemEscolheu(timeAbbr) {
    return picksDaSemana.filter(p => p.time_escolhido === timeAbbr)
  }

  const vidasPorMembro = {}
  for (const m of membros) vidasPorMembro[m.membro_id] = m.vidas_restantes

  const [cardExpandido, setCardExpandido] = useState(null)

  // Situação de cada participante ativo nesta rodada: vida garantida, vida perdida ou aguardando
  const statusRodada = (() => {
    const garantida = [], perdida = [], aguardandoList = []
    const isProcessada = semanaSelecionada?.resultados_processados

    if (isProcessada) {
      // Semana já processada: usa picks (incl. nopick) com vidas_antes para refletir quem estava vivo naquela rodada
      for (const pick of picksDaSemana) {
        if ((pick.vidas_antes ?? 0) <= 0) continue
        const entry = { nome: pick.nome, vidas: pick.vidas_antes, time: pick.time_escolhido }
        if (pick.resultado === 'win') garantida.push(entry)
        else if (pick.resultado === 'loss' || pick.resultado === 'nopick' || pick.resultado === 'draw') perdida.push(entry)
        else aguardandoList.push(entry)
      }
    } else {
      // Semana atual: usa vidas_restantes atuais
      const membrosAtivos = membros.filter(m => (m.vidas_restantes ?? 0) > 0)
      for (const m of membrosAtivos) {
        const pick = picksDaSemana.find(p => p.membro_id === m.membro_id)
        const entry = { nome: m.nome, vidas: m.vidas_restantes, time: pick?.time_escolhido || null }
        if (!pick?.time_escolhido) { perdida.push(entry); continue }
        const jogo = jogos.find(j => j.time_casa === pick.time_escolhido || j.time_visitante === pick.time_escolhido)
        if (!jogo) { aguardandoList.push(entry); continue }
        const res = resultadoDoTime(jogo, pick.time_escolhido)
        if (res === null) aguardandoList.push(entry)
        else if (res === 'venceu') garantida.push(entry)
        else perdida.push(entry)
      }
    }
    return { garantida, perdida, aguardando: aguardandoList, total: garantida.length + perdida.length + aguardandoList.length }
  })()

  if (loadingSemanas) return <div style={s.loading}>Carregando...</div>

  return (
    <div>
      <div style={s.header}>
        <div style={s.title}>Placar da Rodada</div>
        {semanas.length > 0 && (
          <select style={s.select} value={semanaId || ''} onChange={e => setSemanaId(Number(e.target.value))}>
            {semanas.map(sem => (
              <option key={sem.id} value={sem.id}>{labelSemana(sem)}</option>
            ))}
          </select>
        )}
      </div>

      {!semanaSelecionada ? (
        <div style={s.empty}>Nenhuma semana cadastrada ainda.</div>
      ) : !semanaSelecionada.picks_revelados ? (
        <div style={s.banner('#FAEEDA', '#BA7517')}>
          🔒 Esta semana ainda não foi revelada. Volte após o comissário revelar os picks.
        </div>
      ) : (
        <>
          {jogos.length > 0 && (
            <div style={{ marginBottom: '.9rem' }}>
              <div style={s.statGrid}>
                {[
                  { key: 'garantida', lista: statusRodada.garantida, cor: '#3B6D11', label: '✅ Vida garantida' },
                  { key: 'perdida',   lista: statusRodada.perdida,   cor: '#A32D2D', label: '❌ Vida perdida' },
                  { key: 'aguardando',lista: statusRodada.aguardando,cor: '#BA7517', label: '⏳ Aguardando jogo' },
                ].map(({ key, lista, cor, label }) => (
                  <button
                    key={key}
                    style={{ ...s.statCard, cursor: 'pointer', border: cardExpandido === key ? `1.5px solid ${cor}` : '1.5px solid transparent' }}
                    onClick={() => setCardExpandido(cardExpandido === key ? null : key)}
                  >
                    <div style={{ ...s.statValor, color: cor }}>{lista.length}</div>
                    <div style={s.statLabel}>{label}</div>
                    <div style={{ fontSize: 9, color: '#bbb', marginTop: 3 }}>{cardExpandido === key ? '▲' : '▼'}</div>
                  </button>
                ))}
              </div>

              {cardExpandido && (() => {
                const cfg = { garantida: { cor: '#3B6D11', bg: '#EAF3DE', label: 'Vida garantida' }, perdida: { cor: '#A32D2D', bg: '#FCEBEB', label: 'Vida perdida' }, aguardando: { cor: '#BA7517', bg: '#FAEEDA', label: 'Aguardando' } }[cardExpandido]
                const lista = statusRodada[cardExpandido]
                return (
                  <div style={{ background: cfg.bg, borderRadius: 8, padding: '10px 12px', marginTop: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 500, color: cfg.cor, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      {cfg.label} · {lista.length} participante{lista.length !== 1 ? 's' : ''}
                    </div>
                    {lista.length === 0 ? (
                      <div style={{ fontSize: 12, color: '#999' }}>Nenhum participante</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {lista.map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {p.time && <img src={`/logos/${p.time.toLowerCase()}.png`} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />}
                              <span style={{ fontWeight: 500 }}>{p.nome}</span>
                              {p.time ? <span style={{ color: '#888' }}>{p.time}</span> : <span style={{ color: '#bbb' }}>sem pick</span>}
                            </div>
                            <span style={{ color: cfg.cor, fontWeight: 600, fontSize: 11 }}>{'❤️'.repeat(Math.min(p.vidas, 5))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          )}

          <div style={s.toolbar}>
            <button style={s.refreshBtn} onClick={() => carregarJogosEPicks(true)} disabled={loadingJogos}>
              {loadingJogos ? 'Atualizando...' : '↻ Atualizar'}
            </button>
            {atualizadoEm && (
              <span style={s.atualizadoTxt}>
                Atualizado às {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
                {jogos.some(j => j.status === 'STATUS_IN_PROGRESS') ? ' · auto a cada 30s' : ''}
              </span>
            )}
          </div>

          {loadingJogos && jogos.length === 0 ? (
            <div style={s.loading}>Carregando jogos...</div>
          ) : jogos.length === 0 ? (
            <div style={s.empty}>Nenhum jogo encontrado para esta semana.</div>
          ) : (
            <div style={s.jogosList}>
              {(() => {
                const medalhas = ['🥇', '🥈', '🥉']
                const jogosPorPicks = jogos.map(j => ({ id: j.espn_event_id, total: quemEscolheu(j.time_casa).length + quemEscolheu(j.time_visitante).length }))
                const totaisUnicos = [...new Set(jogosPorPicks.map(j => j.total))].sort((a, b) => b - a)
                const medalhaMap = {}
                jogosPorPicks.forEach(j => { const rank = totaisUnicos.indexOf(j.total); if (rank < 3 && j.total > 0) medalhaMap[j.id] = medalhas[rank] })
                return jogos.map(jogo => {
                const st = statusJogo(jogo)
                const expandido = jogoExpandidoId === jogo.espn_event_id
                const quemCasa = quemEscolheu(jogo.time_casa)
                const quemVisitante = quemEscolheu(jogo.time_visitante)
                const totalPicks = quemCasa.length + quemVisitante.length
                const resCasa = resultadoDoTime(jogo, jogo.time_casa)
                const resVisitante = resultadoDoTime(jogo, jogo.time_visitante)
                const corBadge = (res) => res === 'venceu' ? s.pickCountBadgeVenceu : res === 'perdeu' ? s.pickCountBadgePerdeu : s.pickCountBadge
                const medalha = medalhaMap[jogo.espn_event_id]
                return (
                  <div key={jogo.espn_event_id} style={s.jogoCard}>
                    <div style={s.jogoHeader}>
                      {medalha && <span style={s.medalha}>{medalha}</span>}
                      <span style={s.totalPicksBadge}>👥 {totalPicks} escolheram este jogo</span>
                    </div>
                    <button
                      style={s.jogoBtn}
                      onClick={() => setJogoExpandidoId(expandido ? null : jogo.espn_event_id)}
                    >
                      <div style={s.timeCol}>
                        <img src={`/logos/${jogo.time_visitante.toLowerCase()}.png`} alt="" style={s.logo} onError={e => { e.target.style.display = 'none' }} />
                        <span style={s.timeAbbr}>{jogo.time_visitante}</span>
                        <span style={corBadge(resVisitante)}>👥 {quemVisitante.length}</span>
                      </div>
                      <div style={s.placarCol}>
                        {jogo.status !== 'STATUS_SCHEDULED' ? (
                          <div style={s.placar}>{jogo.placar_visitante ?? 0} – {jogo.placar_casa ?? 0}</div>
                        ) : (
                          <div style={s.vsTxt}>@</div>
                        )}
                        <div style={{ ...s.statusTxt, color: st.cor }}>
                          {st.vivo && <span style={s.liveDot} />}
                          {st.texto}
                        </div>
                      </div>
                      <div style={s.timeCol}>
                        <img src={`/logos/${jogo.time_casa.toLowerCase()}.png`} alt="" style={s.logo} onError={e => { e.target.style.display = 'none' }} />
                        <span style={s.timeAbbr}>{jogo.time_casa}</span>
                        <span style={corBadge(resCasa)}>👥 {quemCasa.length}</span>
                      </div>
                      <span style={s.chevron}>{expandido ? '▲' : '▼'}</span>
                    </button>

                    {expandido && (
                      <div style={s.expansao}>
                        <div style={s.colunaPicks}>
                          <div style={s.colunaTitulo}>{jogo.time_visitante} · {quemVisitante.length} escolheram</div>
                          {quemVisitante.length === 0 ? (
                            <div style={s.semPick}>Ninguém escolheu este time</div>
                          ) : (
                            <ul style={s.listaNomes}>
                              {[...quemVisitante].sort((a, b) => (vidasPorMembro[b.membro_id] ?? 0) - (vidasPorMembro[a.membro_id] ?? 0)).map(p => (
                                <li key={p.membro_id} style={s.nomeItem}>
                                  <span>{p.nome}</span>
                                  <span style={s.vidasHearts}>{'❤️'.repeat(Math.min(vidasPorMembro[p.membro_id] ?? 0, 5))}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div style={s.colunaPicks}>
                          <div style={s.colunaTitulo}>{jogo.time_casa} · {quemCasa.length} escolheram</div>
                          {quemCasa.length === 0 ? (
                            <div style={s.semPick}>Ninguém escolheu este time</div>
                          ) : (
                            <ul style={s.listaNomes}>
                              {[...quemCasa].sort((a, b) => (vidasPorMembro[b.membro_id] ?? 0) - (vidasPorMembro[a.membro_id] ?? 0)).map(p => (
                                <li key={p.membro_id} style={s.nomeItem}>
                                  <span>{p.nome}</span>
                                  <span style={s.vidasHearts}>{'❤️'.repeat(Math.min(vidasPorMembro[p.membro_id] ?? 0, 5))}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
              })()}
            </div>
          )}

          {jogos.length > 0 && (
            <div style={s.toolbarRodape}>
              <button style={s.refreshBtn} onClick={() => carregarJogosEPicks(true)} disabled={loadingJogos}>
                {loadingJogos ? 'Atualizando...' : '↻ Atualizar'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: 8, flexWrap: 'wrap' },
  title: { fontSize: 17, fontWeight: 500 },
  select: { padding: '7px 10px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 13, background: 'white', color: '#333' },
  banner: (bg, color) => ({ background: bg, border: `0.5px solid ${color}`, borderRadius: 8, padding: '10px 12px', fontSize: 12, color }),
  loading: { fontSize: 13, color: '#888', padding: '1.5rem 0', textAlign: 'center' },
  empty: { fontSize: 13, color: '#888', padding: '1.5rem 0', textAlign: 'center' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7, marginBottom: '.9rem' },
  statCard: { background: '#f5f5f5', borderRadius: 8, padding: '10px 8px', textAlign: 'center', outline: 'none' },
  statValor: { fontSize: 20, fontWeight: 600 },
  statLabel: { fontSize: 11, color: '#666', marginTop: 2 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: '.8rem', flexWrap: 'wrap' },
  toolbarRodape: { display: 'flex', justifyContent: 'center', marginTop: '1rem' },
  refreshBtn: { padding: '6px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: 'white', fontSize: 12, cursor: 'pointer', color: '#333' },
  atualizadoTxt: { fontSize: 11, color: '#999' },
  jogosList: { display: 'flex', flexDirection: 'column', gap: 8 },
  jogoCard: { background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' },
  jogoHeader: { padding: '8px 12px 0', display: 'flex', alignItems: 'center', gap: 6 },
  medalha: { fontSize: 26, lineHeight: 1 },
  totalPicksBadge: { fontSize: 10, color: '#666', background: '#f5f5f5', borderRadius: 99, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' },
  jogoBtn: { display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', alignItems: 'center', width: '100%', padding: '12px 14px', border: 'none', background: 'transparent', cursor: 'pointer', gap: 8 },
  timeCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 },
  logo: { width: 26, height: 26, objectFit: 'contain' },
  timeAbbr: { fontSize: 14, fontWeight: 500 },
  pickCountBadge: { fontSize: 10, color: '#888', background: '#f0f0f0', borderRadius: 99, padding: '1px 7px' },
  pickCountBadgeVenceu: { fontSize: 10, color: '#3B6D11', background: '#EAF3DE', borderRadius: 99, padding: '1px 7px', fontWeight: 500 },
  pickCountBadgePerdeu: { fontSize: 10, color: '#A32D2D', background: '#FCEBEB', borderRadius: 99, padding: '1px 7px', fontWeight: 500 },
  placarCol: { textAlign: 'center', minWidth: 70 },
  placar: { fontSize: 16, fontWeight: 600, color: '#0A2342' },
  vsTxt: { fontSize: 13, color: '#bbb' },
  statusTxt: { fontSize: 10, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 },
  liveDot: { width: 6, height: 6, borderRadius: '50%', background: '#A32D2D', display: 'inline-block' },
  chevron: { fontSize: 10, color: '#bbb', justifySelf: 'end' },
  expansao: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderTop: '0.5px solid #eee', background: '#fafafa', padding: '12px 14px' },
  colunaPicks: { minWidth: 0 },
  colunaTitulo: { fontSize: 11, fontWeight: 500, color: '#666', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.03em' },
  semPick: { fontSize: 12, color: '#999' },
  listaNomes: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 },
  nomeItem: { fontSize: 13, color: '#333', background: 'white', border: '0.5px solid #eee', borderRadius: 6, padding: '5px 9px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 },
  vidasHearts: { fontSize: 11, letterSpacing: 1, whiteSpace: 'nowrap', flexShrink: 0 },
}
